import { Inject, Injectable, OnDestroy } from '@angular/core';
import { PureAbility } from '@casl/ability';
import { AbilityService } from '@casl/angular';
import { Criteria, interfaces } from '@daas-consulting/core-cross-interfaces';
import * as utils from '@daas-consulting/core-frontend-utils';
import { QueryStringService } from '@daas-consulting/core-frontend-utils';
import * as models from '@daas-consulting/production/remitos/frontend/models';
import * as store from '@daas-consulting/production/remitos/frontend/store';
import {
  RemitosListPageViewContract,
  RemitosListPageViewContractId,
} from '@daas-consulting/production/remitos/frontend/view_contracts';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, Select, Store, ofActionSuccessful } from '@ngxs/store';
import {
  BehaviorSubject,
  combineLatest,
  combineLatestAll,
  map,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';

export { RemitosListPageViewContractId };

@Injectable()
export class RemitosListPageViewModel
  implements RemitosListPageViewContract, OnDestroy {
  @Select(store.list.selectors.RemitosSelectors.totalItems)
  totalItems$!: Observable<number>;

  @Select(store.list.selectors.RemitosSelectors.activePageSize)
  activePageSize$!: Observable<number>;

  @Select(store.list.selectors.RemitosSelectors.activePageIndex)
  activePageIndex$!: Observable<number>;

  @Select(store.list.selectors.RemitosSelectors.pageSizeOptions)
  pageSizeOptions$!: Observable<number[]>;

  @Select(store.list.selectors.RemitosSelectors.activeFilters)
  activeFilters$!: Observable<Criteria[]>;

  @Select(store.list.selectors.RemitosSelectors.filter)
  filter$!: Observable<store.list.models.Filter | null>;

  @Select(store.list.selectors.RemitosSelectors.isResetDisabled)
  isResetDisabled$!: Observable<boolean>;

  @Select(store.list.selectors.RemitosSelectors.isApplyDisabled)
  isApplyDisabled$!: Observable<boolean>;

  @Select(store.detailPreview.selectors.RemitosDetailPreviewSelectors.item)
  item$!: Observable<models.detailPreview.ItemView | null>;

  @Select(store.print.selectors.RemitosPrintSelectors.printData)
  printData$!: Observable<models.print.PrintDataView | null>;

  readonly loadItemSuccessful$: Observable<void>;
  readonly loadPrintDataSuccessful$: Observable<void>;
  readonly printSuccessful$: Observable<void>;
  readonly cancelSuccessful$: Observable<void>;

  readonly showCreate$: Observable<boolean>;
  readonly showPrint$: Observable<boolean>;
  readonly showCancel$: Observable<boolean>;
  readonly showAudit$: Observable<boolean>;
  readonly items$: Observable<models.RemitoListItemModel[]>;

  readonly #store: Store;
  readonly #actions$: Actions;
  readonly #destroy$: Subject<void> = new Subject<void>();
  readonly #loadItemSuccessful$: Subject<void> = new Subject<void>();
  readonly #loadPrintDataSuccessful$: Subject<void> = new Subject<void>();
  readonly #printSuccessful$: Subject<void> = new Subject<void>();
  readonly #cancelSuccessful$: Subject<void> = new Subject<void>();
  readonly #showCreate$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  readonly #showPrint$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  readonly #showCancel$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  readonly #showAudit$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  readonly #queryString: utils.querystring.QueryStringService;

  constructor(
    storeRef: Store,
    actions$: Actions,
    abilityService: AbilityService<PureAbility>,
    @Inject(QueryStringService) queryString: QueryStringService
  ) {
    this.#store = storeRef;
    this.#actions$ = actions$;
    this.#queryString = queryString;
    this.loadItemSuccessful$ = this.#loadItemSuccessful$.asObservable();
    this.loadPrintDataSuccessful$ =
      this.#loadPrintDataSuccessful$.asObservable();
    this.printSuccessful$ = this.#printSuccessful$.asObservable();
    this.cancelSuccessful$ = this.#cancelSuccessful$.asObservable();
    this.showCreate$ = this.#showCreate$.asObservable();
    this.showPrint$ = this.#showPrint$.asObservable();
    this.showCancel$ = this.#showCancel$.asObservable();
    this.showAudit$ = this.#showAudit$.asObservable();
    this.items$ = combineLatest([
      this.#store.select(store.list.selectors.RemitosSelectors.items),
      this.showPrint$,
      this.showCancel$,
    ]).pipe(
      map(([items, showPrint, showCancel]) => {
        return items.map((item) => {
          const rv: models.RemitoListItemModel = {
            ...item,
            canCancel: item.status != 'canceled' && showCancel,
            canPrint: item.status === 'emitted' && showPrint,
          };
          return rv;
        });
      })
    );
    abilityService.ability$
      .pipe(takeUntil(this.#destroy$))
      .subscribe((ability) => {
        this.#showCreate$.next(
          ability.can('create', 'production.remitos.remito')
        );
        this.#showPrint$.next(
          ability.can('print', 'production.remitos.remito')
        );
        this.#showCancel$.next(
          ability.can('cancel', 'production.remitos.remito')
        );
        this.#showAudit$.next(ability.can('view', 'security.audit.audit'));
      });
    this._connectEvents();
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  initFilter(): void {
    const { pageIndex, pageSize, filters } = this.#queryString.get();
    const action = new store.list.actions.SetFullFilter({
      pageIndex,
      pageSize,
      filters: [...filters],
    });
    this.#store.dispatch(action);
  }

  load(item: models.RemitoListItemModel): void {
    this.#store.dispatch(
      new store.detailPreview.actions.GetItem(item.remitoId)
    );
  }

  loadPrint(item: models.RemitoListItemModel): void {
    this.#store.dispatch(new store.print.actions.GetPrintData(item.remitoId));
  }

  confirmPrint(_remitoId: string): void {
    this.#printSuccessful$.next();
  }

  cancel(remitoId: string, canceledReason: string): void {
    this.#store.dispatch(
      new store.list.actions.CancelItem({ remitoId, canceledReason })
    );
  }

  newItem(): void {
    this.#store.dispatch(new Navigate(['/remitos/new']));
  }

  setPagination(pageIndex: number, pageSize: number): void {
    this.#store.dispatch(
      new store.list.actions.SetPagination({ pageIndex, pageSize })
    );
  }

  applyFilter(): void {
    this.#store.dispatch(new store.list.actions.SetFilter());
  }

  resetFilter(): void {
    this.#store.dispatch(new store.list.actions.SetFullFilter());
  }

  removeFilter(filter: Criteria): void {
    this.#store.dispatch(new store.list.actions.RemoveFilter(filter.key));
  }

  setSort(field: string, direction: string): void {
    const action = new store.list.actions.SetOrder({
      field: field,
      type: <interfaces.OrderType>direction,
    });
    this.#store.dispatch(action);
  }

  private _connectEvents(): void {
    this.#actions$
      .pipe(
        ofActionSuccessful(store.detailPreview.actions.GetItemSuccessful),
        takeUntil(this.#destroy$)
      )
      .subscribe(() => this.#loadItemSuccessful$.next());

    this.#actions$
      .pipe(
        ofActionSuccessful(store.print.actions.GetPrintDataSuccessful),
        takeUntil(this.#destroy$)
      )
      .subscribe(() => this.#loadPrintDataSuccessful$.next());

    this.#actions$
      .pipe(
        ofActionSuccessful(store.list.actions.CancelItemSuccessful),
        takeUntil(this.#destroy$)
      )
      .subscribe(() => this.#cancelSuccessful$.next());
  }
}
